import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, Server, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const Home = () => {
  const features = [
    {
      icon: Lock,
      title: '100% Client-Side',
      description: 'All operations run locally in your browser. No server uploads, no data transmission.',
    },
    {
      icon: Eye,
      title: 'Zero Tracking',
      description: 'No cookies, no analytics, no logging. Your privacy is absolute.',
    },
    {
      icon: Server,
      title: 'No Storage',
      description: 'We never store your data. Everything is processed in real-time and discarded.',
    },
    {
      icon: Shield,
      title: 'Military-Grade Encryption',
      description: 'Industry-standard encryption algorithms protect your sensitive information.',
    },
  ];

  const stats = [
    { value: '9', label: 'Security Tools' },
    { value: '0', label: 'Data Stored' },
    { value: '100%', label: 'Privacy' },
  ];

  return (
    <div className="animate-fade-in">
      {/* Hero Section */}
      <section className="relative overflow-hidden hero-pattern animated-bg">
        <div className="container-custom py-20 lg:py-32">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <Badge className="mb-6 glass-card text-sm px-4 py-2" variant="outline">
              <Lock className="w-3 h-3 mr-2" />
              Privacy-First Security Tools
            </Badge>

            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-balance leading-tight">
              Your Privacy is
              <span className="gradient-text"> Non-Negotiable</span>
            </h1>

            {/* Subheading */}
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
              Professional security tools that work entirely in your browser.
              No tracking. No storage. No compromises.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/explore">
                <Button size="lg" className="glass-card hover:glow-primary w-full sm:w-auto">
                  Explore Tools
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/about">
                <Button size="lg" variant="outline" className="glass w-full sm:w-auto">
                  Learn More
                </Button>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4 text-success" />
                <span>Open Source</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4 text-success" />
                <span>No Registration</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4 text-success" />
                <span>Free Forever</span>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"></div>
      </section>

      {/* Stats Section */}
      <section className="border-y border-border/50 bg-card/30 backdrop-blur-sm">
        <div className="container-custom py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl lg:text-5xl font-bold gradient-text mb-2">
                  {stat.value}
                </div>
                <div className="text-muted-foreground text-sm uppercase tracking-wide">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-32">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Built for <span className="gradient-text">Privacy</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              Every tool is designed with one principle: your data never leaves your device.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={index}
                  className="glass-card p-6 text-center group hover:scale-105 transition-smooth"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="inline-flex p-4 glass rounded-xl mb-4 group-hover:glow-primary transition-smooth">
                    <IconComponent className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 lg:py-32 bg-muted/30">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              How It <span className="gradient-text">Works</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              Three simple steps to maximum security and privacy.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                step: '01',
                title: 'Choose Your Tool',
                description: 'Select from our suite of privacy-focused security tools.',
              },
              {
                step: '02',
                title: 'Process Locally',
                description: 'All operations happen in your browser using Web APIs.',
              },
              {
                step: '03',
                title: 'Download & Go',
                description: 'Get your result instantly. Nothing is stored or tracked.',
              },
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="glass-card p-8 text-center">
                  <div className="text-5xl font-bold text-primary/20 mb-4">{item.step}</div>
                  <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-primary/30" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 hero-pattern">
        <div className="container-custom">
          <div className="glass-card p-8 lg:p-12 text-center max-w-3xl mx-auto">
            <Shield className="w-16 h-16 text-primary mx-auto mb-6" />
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Ready to Protect Your Privacy?
            </h2>
            <p className="text-muted-foreground text-lg mb-8">
              Start using our tools today. No registration required.
            </p>
            <Link to="/explore">
              <Button size="lg" className="hover:glow-primary">
                Get Started Free
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
